/** Shared helpers for stock Stories (social research cards). */

import { scoreTierColors, scoreTierLabel as canonScoreTierLabel } from '@/lib/rating-tiers';
import { isMetricUnavailable } from '@/lib/stock-metric-display';
import { getUnadjustedSplitFactor } from '@/lib/stock/cumulativeReturns';

export const STORY_FONT =
  'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
export const STORY_MONO = 'var(--font-jetbrains), "SF Mono", monospace';

/** Empty metric glyph — same family as Med 10Y / PeGap in Briefs. */
export const STORY_NA = '—';

export type StoryKind = 'price' | 'growth' | 'value' | 'strategy';

export type ChartPoint = {
  label: string;
  value: number | null;
  type: 'historical' | 'ltm' | 'forecast';
};

/** @deprecated Prefer scoreTierColors from @/lib/rating-tiers — re-exported for Briefs. */
export function scoreColor(score: number | null | undefined): { text: string; bg: string } {
  return scoreTierColors(score);
}

/** Metric scores are 0–10 (same scale as Key Ratings / header quick stats). */
export function metricScoreColor(score: number | null | undefined): string {
  const sv = typeof score === 'number' ? score : Number(score);
  if (score == null || !isFinite(sv) || sv === 0) return 'rgba(255,255,255,0.5)';
  if (sv >= 7) return '#4ade80';
  if (sv >= 4) return '#facc15';
  return '#f87171';
}

/** Resolve Bastion metric score for a raw field (PEFWD → PEFWDscore, etc.). */
export function metricFieldScore(stock: any, field: string): number | null {
  if (!stock || !field) return null;
  const candidates = [
    `${field}score`,
    `${field}Score`,
    field.endsWith('score') || field.endsWith('Score') ? field : null,
  ].filter(Boolean) as string[];
  for (const key of candidates) {
    const raw = Number(stock?.[key]);
    if (isFinite(raw) && raw > 0) return raw;
  }
  return null;
}

export function metricValueColor(stock: any, field: string): string {
  return metricScoreColor(metricFieldScore(stock, field));
}

export function scoreTierLabel(score: number | null | undefined): string {
  return canonScoreTierLabel(score);
}

export function formatPct(v: number | null | undefined, digits = 0): string {
  if (v == null || !isFinite(v)) return STORY_NA;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(digits)}%`;
}

/** Multiples: 0 / negative are never meaningful (same as Value sidebar). */
export function formatMultiple(v: number | null | undefined, digits = 0): string {
  if (v == null || !isFinite(v) || v <= 0) return STORY_NA;
  return digits === 0 ? `${Math.round(v)}` : v.toFixed(digits);
}

export function formatBillionsShort(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return STORY_NA;
  const abs = Math.abs(v);
  if (abs >= 100) return `$${Math.round(v)}B`;
  if (abs >= 10) return `$${v.toFixed(0)}B`;
  return `$${v.toFixed(1)}B`;
}

/**
 * Stock-field formatters: treat sentinel 0 + weak/missing score as unavailable,
 * matching Value / Ratings2 `isMetricUnavailable`.
 */
export function formatStockPct(stock: any, field: string, digits = 0): string {
  const raw = stock?.[field];
  if (isMetricUnavailable(raw, metricFieldScore(stock, field))) return STORY_NA;
  return formatPct(typeof raw === 'number' ? raw : Number(raw), digits);
}

export function formatStockMultiple(stock: any, field: string, digits = 0): string {
  const raw = stock?.[field];
  if (isMetricUnavailable(raw, metricFieldScore(stock, field))) return STORY_NA;
  return formatMultiple(typeof raw === 'number' ? raw : Number(raw), digits);
}

export function calcCAGR(start: number, end: number, years: number): number | null {
  if (!isFinite(start) || !isFinite(end) || start <= 0 || end <= 0 || years <= 0) return null;
  return (Math.pow(end / start, 1 / years) - 1) * 100;
}

function getFiscalYearLabel(periodEndDateFy: string | null | undefined, offset: number): string {
  if (!periodEndDateFy) return `FY-${offset}`;
  const year = parseInt(periodEndDateFy.substring(0, 4), 10);
  if (isNaN(year)) return `FY-${offset}`;
  return `'${String(year - offset).slice(-2)}`;
}

function getForecastYearLabel(periodEndDateFy: string | null | undefined, offset: number): string {
  if (!periodEndDateFy) return `E${offset}`;
  const year = parseInt(periodEndDateFy.substring(0, 4), 10);
  if (isNaN(year)) return `E${offset}`;
  return `'${String(year + offset).slice(-2)}E`;
}

function getRevenueStartOffset(historicals: Record<string, unknown>): number {
  for (let i = 10; i >= 0; i--) {
    const field = i === 0 ? 'revenue_fy' : `revenue_fy_${i}`;
    if (historicals[field] != null) return i;
  }
  return 0;
}

export function buildMetricSeries(
  historicals: Record<string, unknown> | null | undefined,
  metricKey: string,
  forecastKey?: string,
  divisor = 1,
): ChartPoint[] {
  if (!historicals) return [];
  const fy = historicals.period_end_date_fy as string | null | undefined;
  const points: ChartPoint[] = [];
  // Same window as Financials Value charts (revenue-aligned, up to ~10Y back).
  const startOffset = getRevenueStartOffset(historicals);

  const isMultiple = metricKey === 'pe' || metricKey === 'evebitda' || metricKey === 'evsales';
  const toValue = (num: number | null): number | null => {
    if (num == null || !isFinite(num) || num === 0) return null;
    if (isMultiple && num < 0) return null;
    return num / divisor;
  };

  // Fresh split: FY prices stay pre-split until the next yearly refresh, while
  // today's price is post-split (CRWD 4:1: '26 $441 vs Current $263).
  const splitFactor = metricKey === 'priceclose' ? getUnadjustedSplitFactor(historicals) : 1;

  for (let i = startOffset; i >= 0; i--) {
    const fieldName = i === 0 ? `${metricKey}_fy` : `${metricKey}_fy_${i}`;
    const val = historicals[fieldName];
    const num = val != null ? Number(val) / splitFactor : null;
    points.push({
      label: getFiscalYearLabel(fy, i),
      value: toValue(num),
      type: 'historical',
    });
  }

  if (forecastKey) {
    for (let i = 1; i <= 3; i++) {
      const val = historicals[`${forecastKey}_fy${i}`];
      const num = val != null ? Number(val) : null;
      const value = toValue(num);
      if (value != null || isMultiple) {
        points.push({
          label: getForecastYearLabel(fy, i),
          value,
          type: 'forecast',
        });
      }
    }
  }

  return points;
}

export function bestStrategyBadge(stock: any): { field: string; label: string; score: number } | null {
  const candidates: { field: string; label: string }[] = [
    { field: 'ValueMomentum', label: 'Smart Value' },
    { field: 'GrowthMomentum', label: 'Growth Rockets' },
    { field: 'QualityGrowth', label: 'Quality Compounders' },
    { field: 'GEAR5FScore', label: 'GEAR+' },
  ];
  let best: { field: string; label: string; score: number } | null = null;
  for (const c of candidates) {
    const v = Number(stock?.[c.field]);
    if (!isFinite(v) || v <= 0) continue;
    if (!best || v > best.score) best = { ...c, score: v };
  }
  return best;
}

export const FACTOR_FIELDS = [
  { field: 'PureGrowth', label: 'Growth' },
  { field: 'PureQuality', label: 'Quality' },
  { field: 'PureValue', label: 'Value' },
  { field: 'PureMomentum', label: 'Momentum' },
  { field: 'PureAntifragility', label: 'Risk Shield' },
] as const;
